import { RAIL_STATUS_META, findInCatalogue } from "./stageRailUtils";

export default function RailDependencyTooltip({ stageSpec, flatByCode, catalogue }) {
  const hardDeps = (stageSpec.hard_deps || []).map((d) => ({
    code: d,
    name: (findInCatalogue(catalogue, d) || {}).name || d,
    met: ["complete", "approved"].includes((flatByCode[d] || {}).status),
  }));
  const softDeps = (stageSpec.soft_deps || []).map((d) => ({
    code: d,
    name: (findInCatalogue(catalogue, d) || {}).name || d,
    met: ["complete", "approved"].includes((flatByCode[d] || {}).status),
  }));
  if (!hardDeps.length && !softDeps.length) return null;
  return (
    <div data-testid={`dep-tooltip-${stageSpec.code}`}
         style={{ background: "var(--pf-bg)", border: "1px solid var(--pf-border)",
                  borderRadius: 6, padding: 10, margin: "4px 14px 8px 36px",
                  fontSize: 11 }}>
      {hardDeps.length > 0 && (
        <>
          <div style={{ color: "#EF4444", fontWeight: 600, marginBottom: 4 }}>Hard dependencies</div>
          {hardDeps.map((d) => (
            <div key={d.code} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 2 }}>
              <span>{d.met ? "✅" : "❌"}</span>
              <code style={{ color: "var(--pf-spark)", fontSize: 10 }}>{d.code}</code>
              <span style={{ color: "var(--pf-text-muted)", fontSize: 10 }}>{d.name}</span>
            </div>
          ))}
        </>
      )}
      {softDeps.length > 0 && (
        <>
          <div style={{ color: "#F59E0B", fontWeight: 600, marginTop: 6, marginBottom: 4 }}>Soft dependencies</div>
          {softDeps.map((d) => (
            <div key={d.code} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 2 }}>
              <span>{d.met ? "✅" : "⚠️"}</span>
              <code style={{ color: "var(--pf-spark)", fontSize: 10 }}>{d.code}</code>
              <span style={{ color: "var(--pf-text-muted)", fontSize: 10 }}>{d.name}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// Shared renderer used by all three level-specific rail items. Takes
// pre-resolved meta + Icon to avoid duplication.
// Wave AI — `phaseCell` (from phase visibility matrix) tints the row:
//   { state: "active"|"partial"|"locked", note: str, target_pct: number }
// locked   → 0.35 opacity + dim text
// partial  → 0.75 opacity + amber left border
// active   → normal
export function RailItemButton({ stage, level, isLocked, isActive, meta, Icon, hasSubs, open, onClick, pad, fontSize, phaseCell, gate, progressLabel }) {
  const phState = phaseCell?.state || "active";
  const phLocked = phState === "locked";
  const phPartial = phState === "partial";
  const rowOpacity = isLocked
    ? 0.55
    : (phLocked ? 0.35 : (phPartial ? 0.78 : 1));
  const leftBorder = isActive
    ? "2px solid var(--pf-spark)"
    : (phPartial ? "2px solid #eab308"
        : (phLocked ? "2px solid transparent" : "2px solid transparent"));

  // Sprint 2 Turn 10 — gate-status dot.
  //   emerald = approved (user explicitly approved this stage)
  //   green   = has_output (run, not yet approved)
  //   red     = has_errors
  //   yellow  = runnable (ready to run)
  //   grey    = blocked (upstream missing)
  //   none    = no gate data (graceful fallback)
  let dotColor = null;
  let dotKind = "none";
  if (gate) {
    if (gate.has_errors) { dotColor = "#ef4444"; dotKind = "errors"; }
    else if (gate.approved) { dotColor = "#059669"; dotKind = "approved"; }
    else if (gate.has_output) { dotColor = "#22c55e"; dotKind = "complete"; }
    else if (gate.overridden) { dotColor = "#a78bfa"; dotKind = "overridden"; }
    else if (gate.runnable) { dotColor = "#f59e0b"; dotKind = "runnable"; }
    else if (gate.blocked)  { dotColor = "#9ca3af"; dotKind = "blocked"; }
  }
  const gateTooltip = gate
    ? (gate.has_errors
        ? `Errors — ${stage.code} ran but produced errors`
        : gate.approved
          ? `Approved ✓ — ${stage.code}`
          : gate.has_output
            ? `Run — ${stage.code} (awaiting approval)`
            : gate.overridden
              ? `Override applied — ${stage.code}`
              : gate.blocked && gate.blocked_by?.length
                ? `Blocked — approve ${gate.blocked_by.join(", ")} first`
                : gate.runnable
                  ? `Ready to run — ${stage.code}`
                  : "")
    : "";

  const tooltip = `${stage.code} · ${stage.name}\n${meta.label}`
    + (isLocked ? " · click to see blocking deps" : "")
    + (gateTooltip ? `\n${gateTooltip}` : "")
    + (phaseCell && phState !== "active"
        ? `\nPhase: ${phState.toUpperCase()}${phaseCell.note ? " — " + phaseCell.note : ""}`
        : "");
  return (
    <button type="button"
        data-testid={`stage-rail-${stage.code}`}
        data-phase-state={phState}
        data-gate-status={dotKind}
        onClick={onClick}
        title={tooltip}
        style={{ width: "100%", textAlign: "left", border: "none",
                      background: isActive ? "var(--pf-surface-2)" : "transparent",
                      padding: `${level === 1 ? 8 : 6}px 14px ${level === 1 ? 8 : 6}px ${pad}px`,
                      cursor: "pointer", opacity: rowOpacity,
                      display: "flex", alignItems: "center", gap: 8,
                      color: "var(--pf-text)",
                      borderLeft: leftBorder }}>
      {hasSubs ? <Chev open={open} /> : <span style={{ width: 12 }} />}
      <Icon size={level === 1 ? 13 : 11} color={meta.color} />
      {dotColor && (
        <span data-testid={`stage-rail-${stage.code}-gate-dot`}
              style={{ display: "inline-block",
                       width: 7, height: 7, borderRadius: "50%",
                       background: dotColor, flexShrink: 0 }} />
      )}
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: level === 1 ? 11 : 10,
                            color: phLocked ? "var(--pf-text-dim)" : "var(--pf-spark)",
                            minWidth: level === 3 ? 40 : level === 2 ? 32 : 28 }}>{stage.code}</span>
      <span style={{ fontSize, flex: 1,
                            color: isActive ? "var(--pf-text)" : "var(--pf-text-muted)" }}>
        {stage.name}
      </span>
      {phPartial && (
        <span data-testid={`stage-rail-${stage.code}-partial`}
              style={{ fontSize: 9, color: "#eab308",
                         fontFamily: "'JetBrains Mono', monospace",
                         padding: "1px 4px", border: "1px solid #eab308",
                         borderRadius: 2 }}>PART</span>
      )}
      {progressLabel && (
        <span data-testid={`stage-rail-${stage.code}-progress`}
              style={{ fontSize: 10, color: "var(--pf-text-muted)",
                         fontFamily: "'JetBrains Mono', monospace",
                         padding: "1px 5px",
                         border: "1px solid var(--pf-border)",
                         borderRadius: 3,
                         background: "rgba(255,255,255,0.04)" }}>
          {progressLabel}
        </span>
      )}
      {phLocked && (
        <span data-testid={`stage-rail-${stage.code}-locked`}
              style={{ fontSize: 9, color: "var(--pf-text-dim)",
                         fontFamily: "'JetBrains Mono', monospace",
                         padding: "1px 4px", border: "1px solid var(--pf-border)",
                         borderRadius: 2 }}>LOCKED</span>
      )}
    </button>
  );
}

function Chev({ open }) {
  // Plain inline SVG so we don't import lucide icons in this shared file.
  return (
    <span style={{ display: "inline-block", width: 12, color: "var(--pf-text-dim)",
                   transform: open ? "rotate(90deg)" : "none",
                   transition: "transform .15s" }}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6"></polyline>
      </svg>
    </span>
  );
}

export { RAIL_STATUS_META };
